import { Box, Stack, Typography, Select, MenuItem, FormControl } from "@mui/material";
import { InnerBox } from "@/components/elements/InnerBox";
import { FCC, TOption } from "@/types/common";
import { TCaseMatches } from "@/api/services/main/case/dtos/get-case.dto";
import { TGetCaseMatchesReqDto } from "@/api/services/main/case/dtos/get-case-matches.dto";
import { text, blue, orange } from "@/theme/colors";
import { CaseEditMatchTable } from "@/features/case/components/CaseEdit/CaseEditMatchTable";
import { useCaseMatches } from "@/api/services/main/case";
import { useParams, useSearchParams } from "react-router-dom";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import { Pagination } from "@/components/elements/Pagination";
import { useVisible } from "@/hooks/useVisible";
import { TalentListModal } from "@/features/case/components/CaseEdit/TalentListModal";
import { useJobInfo } from "@/api/services/main/job";
import { SharedPagination } from "@/components/elements/Pagination/SharedPagination";
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { IconFormatListBulleted } from "@/assets/icons";
import { Button } from "@/components/elements/Button";
import { dayjs } from "@/utils/dayjs";
import { DATE_FORMAT } from "@/constants/datetime";
import { ConfirmDeleteModal } from "@/components/commons/ConfirmDeleteModal";
import {
  TCaseEditMatchFilterFormExtended,
  TCaseEditMatchDisplayColumnSetting,
  defaultCaseEditMatchFilterValues,
  defaultCaseEditMatchDisplayColumns,
  isEmptyCaseEditMatchFilterValue,
  normalizeCaseEditMatchMultiSelectFields,
  reconcileCaseEditMatchDisplayColumns,
  stripEmptyCaseEditMatchFilterFields,
  LOCKED_CASE_EDIT_MATCH_COLUMN_IDS,
} from "@/features/case/components/CaseEdit/case-edit-match-filter-shared";
import { CaseEditMatchFilterConditionPanel } from "@/features/case/components/CaseEdit/CaseEditMatchFilterConditionPanel";
import { FilterConditionToolbar, TSavedCondition as TToolbarSavedCondition } from "@irohana-dev/components";
import { useDeleteSavedCondition, useSavedConditionList } from "@/api/services/main/saved-condition";
import { readSessionFilterState, writeSessionFilterState } from "@/utils/session-filter-state";
import { applyColumnSettings, enforceLockedColumns, hasDisplayColumnsChanged, resolveSavedConditionColumns } from "@/components/commons/filter-condition/filter-condition-adapter-kit";
import { toast } from "@/utils/toast";
import { showError } from "@/utils/error";
import { black } from "@/theme/colors";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { CaseEditMatchAppliedConditionDetailPopover } from "@/features/case/components/CaseEdit/CaseEditMatchAppliedConditionDetailPopover";
import { CaseEditMatchEditSavedConditionModal } from "@/features/case/components/CaseEdit/CaseEditMatchEditSavedConditionModal";
import {
  CaseMatchSavedConditionsListModal,
  TCaseMatchSavedCondition,
} from "@/features/case/components/CaseMatches/CaseMatchSavedConditionsListModal";
import { getCaseMatchStorageKeys } from "@/features/case/constants/storage-keys";
import { filterConditionShareColor } from "@/components/commons/filter-condition/filter-condition-share-color";

const STORAGE_KEY_PREFIX = "case_edit";

/** Convert popover filter values to backend query params */
const buildQueryFromFilters = (filters: TCaseEditMatchFilterFormExtended): Partial<TGetCaseMatchesReqDto> => {
  const query: Partial<TGetCaseMatchesReqDto> = {};
  if (filters.status.length > 0) query.status = filters.status.map(Number);
  if (filters.talentNumber) query.talentNumber = filters.talentNumber;
  if (filters.fullName) query.fullName = filters.fullName;
  if (filters.nickName) query.nickName = filters.nickName;
  if (filters.nationalityId.length > 0) query.nationalityId = filters.nationalityId.map(Number);
  if (filters.gender.length > 0) query.gender = filters.gender.map(Number);
  if (filters.residenceStatusId.length > 0) query.residenceStatusId = filters.residenceStatusId.map(Number);
  if (filters.languageTestLevelId.length > 0) query.languageTestLevelId = filters.languageTestLevelId.map(Number);
  if (filters.personInChargeId.length > 0) query.personInChargeId = filters.personInChargeId.map(Number);
  if (filters.ageFrom || filters.ageTo) {
    const from = filters.ageFrom || "";
    const to = filters.ageTo || "";
    query.age = [`${from}-${to}`];
  }
  if (filters.periodOfStayFrom) query.periodOfStayFrom = filters.periodOfStayFrom;
  if (filters.periodOfStayTo) query.periodOfStayTo = filters.periodOfStayTo;
  if (filters.interviewDate.length > 0) {
    query.interviewDates = filters.interviewDate;
  }
  if (filters.tagIds.length > 0) query.tagIds = filters.tagIds.map(String);
  return query;
};


type TProps = {
  caseMatchesData: TCaseMatches[];
  total: number;
  countItem: number;
  totalPage: number;
  formatTotal: (total: number) => string;
  onStatusMapChange?: (statusMap: Record<number, number>) => void;
  onTalentStatusMapChange?: (talentStatusMap: Record<number, number>) => void;
  onDeleteCaseMatch?: (caseMatchId: number) => Promise<void>;
  onCaseMatchMissingFieldsUpdate?: (updates: Record<number, boolean>) => void;
  onAutoSaveCaseMatchStatus?: (caseMatchId: number, newStatus?: number, newTalentStatus?: number, previousStatus?: number) => Promise<void>;
  canEdit?: boolean;
  isCompleted?: boolean;
  caseName?: string;
  isSharedView?: boolean;
  shareUrl?: string;
  sharedPageSize?: number;
  sharedPageSizeOptions?: number[];
  onSharedPageSizeChange?: (size: number) => void;
  sharedCurrentPage?: number;
  onSharedPageChange?: (page: number) => void;
  currentSort?: string;
  onSort?: (sortKey: string) => void;
  /** Current filter params applied to the API query */
  filterParams?: Partial<TGetCaseMatchesReqDto>;
  /** Callback to update filter params — triggers refetch in parent */
  onFilterParamsChange?: (params: Partial<TGetCaseMatchesReqDto>) => void;
};

export const CaseEditMatches: FCC<TProps> = ({
  caseMatchesData,
  total,
  countItem,
  totalPage,
  formatTotal,
  onStatusMapChange,
  onTalentStatusMapChange,
  onDeleteCaseMatch,
  onCaseMatchMissingFieldsUpdate,
  onAutoSaveCaseMatchStatus,
  canEdit = true,
  isCompleted = false,
  caseName,
  isSharedView = false,
  shareUrl,
  sharedPageSize,
  sharedPageSizeOptions,
  onSharedPageSizeChange,
  sharedCurrentPage,
  onSharedPageChange,
  currentSort,
  onSort,
  filterParams,
  onFilterParamsChange,
}) => {
  const { jobId, caseId } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const { data: jobInfoResponse } = useJobInfo(Number(jobId));
  const jobInfo = jobInfoResponse?.data ?? {};
  const jobResidenceStatusIds = useMemo(
    () => (jobInfo?.residenceStatusIds ?? []).map(Number),
    [jobInfo?.residenceStatusIds],
  );
  const talentListModalVisible = useVisible(false);

  const storageKeys = useMemo(() => getCaseMatchStorageKeys(STORAGE_KEY_PREFIX), []);
  const savedConditionPageType = `${STORAGE_KEY_PREFIX}_match`;

  // Fetch ALL case matches without filter params — used for stable autocomplete suggestions
  // so that talentNumber / fullName / nickName / interviewDate options are not affected by active filters
  const { data: allMatchesResponse } = useCaseMatches(Number(caseId), { page: 1, perPage: 9999 });

  const unfilteredListOptions = useMemo(() => {
    const matches: TCaseMatches[] = allMatchesResponse?.data ?? [];
    const talentNumberSet = new Set<string>();
    const fullNameSet = new Set<string>();
    const nickNameSet = new Set<string>();
    const interviewDateSet = new Set<string>();
    matches.forEach((match) => {
      if (match.talent?.talentNumber) talentNumberSet.add(match.talent.talentNumber);
      if (match.talent?.fullName) fullNameSet.add(match.talent.fullName);
      if (match.talent?.nickName) nickNameSet.add(match.talent.nickName);
      if (match.interviewDate) {
        const formatted = dayjs(match.interviewDate).format("YYYY-MM-DD");
        if (formatted !== "Invalid Date") interviewDateSet.add(formatted);
      }
    });
    const toSortedOptions = (set: Set<string>): TOption[] =>
      Array.from(set).sort().map((v) => ({ label: v, value: v }));
    return {
      talentNumber: toSortedOptions(talentNumberSet),
      fullName: toSortedOptions(fullNameSet),
      nickName: toSortedOptions(nickNameSet),
      interviewDate: Array.from(interviewDateSet).sort().map((date) => ({ label: dayjs(date).format(DATE_FORMAT), value: date })),
    };
  }, [allMatchesResponse?.data]);

  // Advanced filter state — restore from localStorage
  const [advancedFilters, setAdvancedFilters] = useState<TCaseEditMatchFilterFormExtended | null>(() => {
    const saved = localStorage.getItem(storageKeys.advancedFilters);
    if (saved) {
      try {
        const parsed = JSON.parse(saved);
        return {
          ...defaultCaseEditMatchFilterValues,
          ...normalizeCaseEditMatchMultiSelectFields(parsed as Partial<Record<string, unknown>>),
        } as TCaseEditMatchFilterFormExtended;
      } catch {
        return null;
      }
    }
    return null;
  });

  const [displayColumns, setDisplayColumns] = useState<TCaseEditMatchDisplayColumnSetting[]>(() => {
    const stored = localStorage.getItem(storageKeys.displayColumns);
    if (stored) {
      try {
        return enforceLockedColumns(reconcileCaseEditMatchDisplayColumns(JSON.parse(stored)), LOCKED_CASE_EDIT_MATCH_COLUMN_IDS);
      } catch {
        /* ignore */
      }
    }
    return [...defaultCaseEditMatchDisplayColumns];
  });

  const [filterAnchorEl, setFilterAnchorEl] = useState<HTMLElement | null>(null);
  const [isClearConfirmOpen, setIsClearConfirmOpen] = useState(false);

  // Saved condition state
  const savedConditionsModal = useVisible();
  const [filterMode, setFilterMode] = useState<"create" | "edit">("create");
  const { data: savedConditionsData } = useSavedConditionList(savedConditionPageType);
  const [isDeleteConfirmOpen, setIsDeleteConfirmOpen] = useState(false);
  const deleteSavedCondition = useDeleteSavedCondition();
  const [appliedConditionName, setAppliedConditionName] = useState<string>(() => localStorage.getItem(storageKeys.appliedConditionName) ?? "");
  const [appliedConditionId, setAppliedConditionId] = useState<number | null>(() => {
    const saved = localStorage.getItem(storageKeys.appliedConditionId);
    if (saved) {
      const parsed = Number(saved);
      return isNaN(parsed) ? null : parsed;
    }
    return null;
  });
  const [conditionDetailAnchorEl, setConditionDetailAnchorEl] = useState<HTMLElement | null>(null);

  const navigateFirstPage = useCallback(() => {
    const params = new URLSearchParams(searchParams);
    params.set("page", "1");
    setSearchParams(params, { replace: true });
  }, [searchParams, setSearchParams]);

  const handleApplyAdvancedFilters = useCallback(
    (filters: TCaseEditMatchFilterFormExtended) => {
      const hasAnyFilter = Object.values(filters).some((v) => !isEmptyCaseEditMatchFilterValue(v));
      if (hasAnyFilter) {
        setAdvancedFilters(filters);
        localStorage.setItem(storageKeys.advancedFilters, JSON.stringify(stripEmptyCaseEditMatchFilterFields(filters)));
        onFilterParamsChange?.(buildQueryFromFilters(filters));
      } else {
        setAdvancedFilters(null);
        localStorage.removeItem(storageKeys.advancedFilters);
        onFilterParamsChange?.({});
      }
      navigateFirstPage();
    },
    [navigateFirstPage, storageKeys, onFilterParamsChange],
  );

  const handleApplyDisplaySettings = useCallback(
    (columns: TCaseEditMatchDisplayColumnSetting[]) => {
      setDisplayColumns(enforceLockedColumns(columns, LOCKED_CASE_EDIT_MATCH_COLUMN_IDS));
      localStorage.setItem(storageKeys.displayColumns, JSON.stringify(columns));
    },
    [storageKeys],
  );

  const handleClearAppliedFilter = useCallback(() => {
    setAdvancedFilters(null);
    setAppliedConditionName("");
    setAppliedConditionId(null);
    handleApplyDisplaySettings([...defaultCaseEditMatchDisplayColumns]);
    setIsClearConfirmOpen(false);
    localStorage.removeItem(storageKeys.advancedFilters);
    localStorage.removeItem(storageKeys.appliedConditionName);
    localStorage.removeItem(storageKeys.appliedConditionId);
    onFilterParamsChange?.({});
  }, [storageKeys, onFilterParamsChange, handleApplyDisplaySettings]);

  const handleApplySavedCondition = useCallback(
    (condition: TCaseMatchSavedCondition) => {
      const normalized = {
        ...defaultCaseEditMatchFilterValues,
        ...normalizeCaseEditMatchMultiSelectFields(condition.filters as unknown as Partial<Record<string, unknown>>),
      };
      handleApplyAdvancedFilters(normalized as TCaseEditMatchFilterFormExtended);
      setAppliedConditionName(condition.title);
      setAppliedConditionId(condition.id);
      localStorage.setItem(storageKeys.appliedConditionName, condition.title);
      localStorage.setItem(storageKeys.appliedConditionId, String(condition.id));
    },
    [handleApplyAdvancedFilters, storageKeys],
  );

  const hasSeededDefault = useRef(readSessionFilterState<boolean>(storageKeys.defaultSeeded) ?? false);

  useEffect(() => {
    if (isSharedView || hasSeededDefault.current || !savedConditionsData) return;
    hasSeededDefault.current = true;
    writeSessionFilterState(storageKeys.defaultSeeded, true);

    const fallback = savedConditionsData.data.find((item) => item.isDefault);
    if (!fallback) return;

    handleApplySavedCondition({
      id: Number(fallback.id),
      title: fallback.title,
      filters: fallback.filters,
    } as TCaseMatchSavedCondition);
    handleApplyDisplaySettings(resolveSavedConditionColumns(fallback, defaultCaseEditMatchDisplayColumns));
  }, [savedConditionsData]);

  const handleConditionTitleChange = useCallback(
    (_id: number, newTitle: string) => {
      setAppliedConditionName(newTitle);
      localStorage.setItem(storageKeys.appliedConditionName, newTitle);
    },
    [storageKeys],
  );

  const justSavedConditionRef = useRef(false);

  const handleApplyFiltersFromPanel = useCallback(
    (filters: TCaseEditMatchFilterFormExtended) => {
      handleApplyAdvancedFilters(filters);
      if (justSavedConditionRef.current) {
        justSavedConditionRef.current = false;
        return;
      }
      if (filterMode === "create") {
        setAppliedConditionName("");
        setAppliedConditionId(null);
        localStorage.removeItem(storageKeys.appliedConditionName);
        localStorage.removeItem(storageKeys.appliedConditionId);
      }
    },
    [handleApplyAdvancedFilters, filterMode, storageKeys],
  );

  const handleConditionSaved = useCallback(
    (id: number, title: string) => {
      justSavedConditionRef.current = true;
      setAppliedConditionName(title);
      setAppliedConditionId(id);
      localStorage.setItem(storageKeys.appliedConditionName, title);
      localStorage.setItem(storageKeys.appliedConditionId, String(id));
      setFilterMode("edit");
    },
    [storageKeys],
  );

  const appliedCondition =
    filterMode === "edit" && appliedConditionId
      ? savedConditionsData?.data.find((item) => Number(item.id) === Number(appliedConditionId))
      : undefined;

  const toolbarConditions = useMemo<TToolbarSavedCondition[]>(
    () =>
      (savedConditionsData?.data ?? []).map((item) => ({
        id: Number(item.id),
        name: item.title,
        shareScope: item.shareScope ?? "self",
        isDefault: item.isDefault ?? false,
        badgeColor: item.defaultColor ?? undefined,
      })),
    [savedConditionsData],
  );

  const handleSelectConditionFromToolbar = useCallback(
    (id: string | number) => {
      const picked = savedConditionsData?.data.find((item) => Number(item.id) === Number(id));
      if (!picked) return;
      handleApplySavedCondition({
        id: Number(picked.id),
        title: picked.title,
        filters: picked.filters,
      } as TCaseMatchSavedCondition);
      if (picked.includeColumnSettings && picked.columnSettings) {
        const settings = picked.columnSettings as { order?: string[]; hidden?: string[] };
        handleApplyDisplaySettings(
          applyColumnSettings(displayColumns, settings.order ?? [], settings.hidden ?? [], defaultCaseEditMatchDisplayColumns),
        );
      }
    },
    [savedConditionsData, handleApplySavedCondition, handleApplyDisplaySettings, displayColumns],
  );

  const handleDeleteAppliedCondition = useCallback(async () => {
    if (!appliedConditionId) return;
    try {
      await deleteSavedCondition.mutateAsync(appliedConditionId);
      toast.success("条件を削除しました。");
      handleClearAppliedFilter();
    } catch (e) {
      showError(e);
    } finally {
      setIsDeleteConfirmOpen(false);
    }
  }, [appliedConditionId, deleteSavedCondition, handleClearAppliedFilter]);

  const activeFilterCount = useMemo(() => {
    if (!advancedFilters) return 0;
    const { periodOfStayFrom, periodOfStayTo, ageFrom, ageTo, ...rest } = advancedFilters;
    let count = Object.values(rest).filter((v) => !isEmptyCaseEditMatchFilterValue(v)).length;
    if (periodOfStayFrom || periodOfStayTo) count += 1;
    if (ageFrom || ageTo) count += 1;
    return count;
  }, [advancedFilters]);

  return (
    <InnerBox mt={4} showDivider={false}>
      {!isSharedView && (
        <Box sx={{ mb: 2 }}>
          <Typography variant="sub16Bold">マッチング一覧</Typography>
        </Box>
      )}

      {/* Filter top bar */}
      {!isSharedView && (
        <Box sx={{ mt: 2, mb: 2, display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <Stack direction="row" alignItems="center" spacing={2}>
            <Typography variant="body14Semi">
              全 {formatTotal(total)} 件中 {countItem} 件を表示
            </Typography>

            <FilterConditionToolbar
              onOpenFilter={() => {
                setFilterMode("create");
                setFilterAnchorEl(document.body);
              }}
              activeCount={
                activeFilterCount || Number(Boolean(appliedConditionId) || hasDisplayColumnsChanged(displayColumns, defaultCaseEditMatchDisplayColumns))
              }
              conditions={toolbarConditions}
              selectedId={appliedConditionId ?? undefined}
              onSelect={handleSelectConditionFromToolbar}
              onClearSelection={() => setIsClearConfirmOpen(true)}
              onEdit={() => {
                setFilterMode("edit");
                setFilterAnchorEl(document.body);
              }}
              onDelete={() => setIsDeleteConfirmOpen(true)}
              shareColor={filterConditionShareColor}
            />

            {appliedConditionName && (
              <Box
                component="span"
                onClick={(e: React.MouseEvent<HTMLElement>) => setConditionDetailAnchorEl(e.currentTarget)}
                sx={{ display: "inline-flex", alignItems: "center", cursor: "pointer" }}
              >
                <InfoOutlinedIcon sx={{ color: black[80], fontSize: 16 }} />
              </Box>
            )}
          </Stack>

          <Stack direction="row" alignItems="center" spacing={2}>
            <Button
              type="button"
              variant="outline"
              size="sm"
              startIcon={<IconFormatListBulleted width={20} height={20} />}
              onClick={savedConditionsModal.open}
            >
              条件一覧
            </Button>
          </Stack>
        </Box>
      )}

      {/* Shared view simple count */}
      {isSharedView && (
        <Stack direction="row" mt={2} mb={2}>
          <Typography variant="body14Semi" color={text.primary}>
            全 {formatTotal(total)} 件中 {countItem} 件を表示
          </Typography>
        </Stack>
      )}

      <CaseEditMatchTable
        rows={caseMatchesData}
        totalCount={total}
        onStatusMapChange={onStatusMapChange}
        onTalentStatusMapChange={onTalentStatusMapChange}
        onDeleteCaseMatch={onDeleteCaseMatch}
        onCaseMatchMissingFieldsUpdate={onCaseMatchMissingFieldsUpdate}
        onAutoSaveCaseMatchStatus={onAutoSaveCaseMatchStatus}
        canEdit={canEdit}
        caseId={caseId}
        caseName={caseName}
        jobClassificationIds={jobInfo?.companySpecificBusinessClassificationIds ?? []}
        jobResidenceStatusIds={jobResidenceStatusIds}
        isSharedView={isSharedView}
        shareUrl={shareUrl}
        currentSort={currentSort}
        onSort={onSort}
        displayColumns={displayColumns}
        filterParams={filterParams}
      />

      {!isCompleted && (
        <Stack
          direction="row"
          alignItems="center"
          onClick={() => canEdit && talentListModalVisible.open()}
          sx={{ cursor: canEdit ? "pointer" : "not-allowed", opacity: canEdit ? 1 : 0.5, mt: 1, width: "fit-content" }}
        >
          <AddCircleOutlineIcon sx={{ color: blue[80], fontSize: 20 }} />
          &nbsp;
          <Typography variant="cap12">新規人材を追加</Typography>
        </Stack>
      )}

      <Stack mt={3} direction="row" alignItems="center" justifyContent="flex-start" width="100%" gap={2}>
        {isSharedView && sharedPageSizeOptions && onSharedPageSizeChange ? (
          <FormControl size="small">
            <Select value={sharedPageSize} onChange={(e) => onSharedPageSizeChange(Number(e.target.value))} sx={{ minWidth: 80, height: 36 }}>
              {sharedPageSizeOptions.map((size) => (
                <MenuItem key={size} value={size}>
                  {size}件
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        ) : (
          <div />
        )}
        {isSharedView && sharedCurrentPage && onSharedPageChange ? (
          <SharedPagination totalPage={totalPage} page={sharedCurrentPage} onChangePage={onSharedPageChange} />
        ) : (
          <Pagination totalPage={totalPage} />
        )}
      </Stack>

      {isCompleted && !isSharedView && (
        <Stack mt={3}>
          <Typography color={orange[80]} variant="cap12">
            全てのマッチングがクローズ状態となったため、新しい人材をマッチングする場合は右下の「新しい案件で再募集」を選択してください。
          </Typography>
        </Stack>
      )}

      {!isSharedView && (
        <>
          <TalentListModal
            isOpen={talentListModalVisible.visible}
            onClose={() => talentListModalVisible.close()}
            companySpecificIndustryId={jobInfo?.companySpecificIndustryId ?? undefined}
            caseId={caseId ? Number(caseId) : undefined}
            jobId={jobId ? Number(jobId) : undefined}
          />

          {/* Filter popover */}
          <CaseEditMatchFilterConditionPanel
            isOpen={Boolean(filterAnchorEl)}
            onClose={() => setFilterAnchorEl(null)}
            mode={filterMode}
            onApplyFilters={handleApplyFiltersFromPanel}
            onApplyDisplaySettings={handleApplyDisplaySettings}
            initialFilters={filterMode === "edit" ? advancedFilters || undefined : defaultCaseEditMatchFilterValues}
            initialDisplayColumns={displayColumns}
            columnsStorageKey={storageKeys.displayColumns}
            pageType={savedConditionPageType}
            appliedConditionId={filterMode === "edit" ? appliedConditionId : null}
            appliedConditionName={filterMode === "edit" ? appliedConditionName : ""}
            appliedConditionShareScope={appliedCondition?.shareScope}
            appliedConditionIsDefault={appliedCondition?.isDefault}
            appliedConditionIncludeColumnSettings={appliedCondition?.includeColumnSettings}
            appliedConditionColumnSettings={appliedCondition?.columnSettings as { order?: string[]; hidden?: string[] } | null | undefined}
            onConditionSaved={handleConditionSaved}
            interviewDateOptions={unfilteredListOptions.interviewDate}
            talentNumberOptions={unfilteredListOptions.talentNumber}
            fullNameOptions={unfilteredListOptions.fullName}
            nickNameOptions={unfilteredListOptions.nickName}
          />

          {/* Saved conditions list modal */}
          <CaseMatchSavedConditionsListModal
            isOpen={savedConditionsModal.visible}
            onClose={savedConditionsModal.close}
            onApply={handleApplySavedCondition}
            pageType={savedConditionPageType}
            appliedConditionId={appliedConditionId}
            onConditionTitleChange={handleConditionTitleChange}
            DetailPopoverComponent={CaseEditMatchAppliedConditionDetailPopover}
            EditModalComponent={useMemo(
              () => (props: { isOpen: boolean; onClose: () => void; onSave: (...args: unknown[]) => void; initialTitle: string; initialFilters: unknown }) => (
                <CaseEditMatchEditSavedConditionModal {...props} onSave={props.onSave as (title: string, filters: TCaseEditMatchFilterFormExtended) => void} initialFilters={props.initialFilters as TCaseEditMatchFilterFormExtended} interviewDateOptions={unfilteredListOptions.interviewDate} />
              ),
              [unfilteredListOptions.interviewDate],
            )}
          />

          {/* Applied condition detail popover */}
          <CaseEditMatchAppliedConditionDetailPopover
            anchorEl={conditionDetailAnchorEl}
            onClose={() => setConditionDetailAnchorEl(null)}
            filterValues={advancedFilters ?? null}
          />

          <ConfirmDeleteModal
            open={isClearConfirmOpen}
            onClose={() => setIsClearConfirmOpen(false)}
            onOk={handleClearAppliedFilter}
            message="設定した条件がすべてクリアされますがよろしいですか？"
            title=""
          />
          <ConfirmDeleteModal
            open={isDeleteConfirmOpen}
            onClose={() => setIsDeleteConfirmOpen(false)}
            onOk={handleDeleteAppliedCondition}
            message="この条件を削除してもよろしいですか？"
            title=""
          />
        </>
      )}
    </InnerBox>
  );
};
